"use client"

/** Tier digest strip + grouped result sections for Analysis (candidates direction). */
import {
  Badge,
  Button,
  SMART_ANALYSIS_TIER_LABELS,
  PUBLIC_MATCH_QUALITY_LABELS,
  cn,
} from '@yeshuman/sdk'
import type { SmartAnalysisTier, SmartAnalysisTierCounts } from '@yeshuman/sdk'
import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'


/** One-line verdict strip using the public Strong / Good labels. */
export function SmartAnalysisTierDigest({
  counts,
  className,
}: {
  counts: SmartAnalysisTierCounts
  className?: string
}) {
  const total = counts.top + counts.compelling + counts.precluded
  if (total === 0) return null

  const strongLabel =
    counts.top === 1 ? PUBLIC_MATCH_QUALITY_LABELS.strong : 'Strong matches'
  const goodLabel =
    counts.compelling === 1 ? PUBLIC_MATCH_QUALITY_LABELS.good : 'Good matches'

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="status"
      aria-label="Analysis verdict"
    >
      {counts.top > 0 ? (
        <Badge variant="successSoft" className="tabular-nums">
          {counts.top} {strongLabel}
        </Badge>
      ) : null}
      {counts.compelling > 0 ? (
        <Badge variant="secondarySoft" className="tabular-nums">
          {counts.compelling} {goodLabel}
        </Badge>
      ) : null}
      {counts.precluded > 0 ? (
        <Badge
          variant="outline"
          className="tabular-nums border-border bg-muted/50 text-muted-foreground"
        >
          {counts.precluded} not recommended
        </Badge>
      ) : null}
      {counts.top === 0 && counts.compelling === 0 ? (
        <span className="text-sm text-muted-foreground">
          No strong matches this run — consider adjusting your instructions.
        </span>
      ) : null}
    </div>
  )
}

const TIER_DESCRIPTIONS: Record<SmartAnalysisTier, string | undefined> = {
  top: 'Best fit for this role based on the analysis.',
  compelling: 'Close on most criteria — a good match worth reviewing.',
  precluded: 'Scored low or raised concerns during the analysis.',
}

/** Grouped tier section with a heading; `collapsible` starts closed (Not recommended). */
export function SmartAnalysisTierSection({
  tier,
  count,
  collapsible = false,
  children,
}: {
  tier: SmartAnalysisTier
  count: number
  collapsible?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(!collapsible)
  if (count === 0) return null

  const label = SMART_ANALYSIS_TIER_LABELS[tier]
  const description = TIER_DESCRIPTIONS[tier]
  const heading = (
    <span className="flex items-baseline gap-2">
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <span className="text-sm tabular-nums text-muted-foreground">{count}</span>
    </span>
  )

  return (
    <section aria-label={`${label} (${count})`} className="space-y-2">
      {collapsible ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 h-auto px-2 py-1"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
          {heading}
        </Button>
      ) : (
        heading
      )}
      {open ? (
        <>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
          {children}
        </>
      ) : null}
    </section>
  )
}
